import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { CreatePricingTierData, UpdatePricingTierData } from '@/types/partners';
import { PricingTier } from '@/types/partners';


// API functions
const fetchPricingTiers = async (): Promise<PricingTier[]> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/pricing`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch pricing tiers');
  }

  return response.json();
};

const upsertPricingTier = async (data: CreatePricingTierData & { id?: string }): Promise<PricingTier> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  // If we have an ID, use PUT to update, otherwise POST to create
  const method = data.id ? 'PUT' : 'POST';
  const url = data.id 
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/pricing/${data.id}`
    : `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/pricing`;

  // Remove id from body for PUT requests (it's in the URL)
  const { id, ...bodyData } = data;

  const response = await fetch(url, {
    method,
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(bodyData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to ${method === 'PUT' ? 'update' : 'create'} pricing tier`);
  }

  return response.json();
};

const updatePricingTier = async ({ id, data }: { id: string; data: UpdatePricingTierData }): Promise<PricingTier> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/pricing/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update pricing tier');
  }

  return response.json();
};

const deletePricingTier = async (id: string): Promise<{ success: boolean }> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/pricing/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete pricing tier');
  }

  return response.json();
};


const fetchPlanSubscribers = async (planId: string): Promise<any[]> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-settings/subscription-plans/${planId}/subscribers`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch subscribers');
  }

  const data = await response.json();
  return data.subscribers || [];
};

// Hooks
export const usePricingTiers = () => {
  return useQuery({
    queryKey: ['partner', 'pricing-tiers'],
    queryFn: fetchPricingTiers,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreatePricingTier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upsertPricingTier,
    onSuccess: () => {
      // Invalidate and refetch pricing tiers
      queryClient.invalidateQueries({ queryKey: ['partner', 'pricing-tiers'] });
    },
  });
};

export const useUpdatePricingTier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePricingTier,
    onSuccess: () => {
      // Invalidate and refetch pricing tiers
      queryClient.invalidateQueries({ queryKey: ['partner', 'pricing-tiers'] });
    },
  });
};

export const useDeletePricingTier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePricingTier,
    onSuccess: () => {
      // Invalidate and refetch pricing tiers
      queryClient.invalidateQueries({ queryKey: ['partner', 'pricing-tiers'] });
    },
  });
};

export const usePlanSubscribers = (planId: string | null) => {
  return useQuery({
    queryKey: ['partner', 'subscription-plans', planId, 'subscribers'],
    queryFn: () => fetchPlanSubscribers(planId!),
    enabled: !!planId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Fetch partner subscription links with permanent checkout links
const fetchPartnerSubscriptionLinks = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-management?action=subscription-plans`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch subscription links');
  }

  return response.json();
};

export const usePartnerSubscriptionLinks = () => {
  return useQuery({
    queryKey: ['partner', 'subscription-links'],
    queryFn: fetchPartnerSubscriptionLinks,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
